import React, { useMemo, useState } from 'react';
import AnalyticsSettingsPanel, { useAnalyticsSettings, SettingsButton } from '../common/AnalyticsSettings';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import TimeTrackerHelpModal from './TimeTrackerHelpModal';

// Available metrics for personalization
const AVAILABLE_METRICS = [
  { id: 'totalHours', label: 'Total Hours', icon: '⏱️', defaultVisible: true },
  { id: 'teamSize', label: 'Team Size', icon: '👥', defaultVisible: true },
  { id: 'regularHours', label: 'Regular Hours', icon: '✅', defaultVisible: true },
  { id: 'overtimeHours', label: 'Overtime Hours', icon: '🔥', defaultVisible: true },
  { id: 'hoursBreakdown', label: 'Hours Breakdown', icon: '📊', defaultVisible: true },
];

// Available charts for personalization
const AVAILABLE_CHARTS = [
  { id: 'cumulativeHours', label: 'Cumulative Hours', icon: '📈', defaultVisible: true, allowTypeChange: false },
  { id: 'hoursByTrade', label: 'Hours by Trade', icon: '🔧', defaultVisible: true, defaultType: 'bar' },
  { id: 'hoursByDivision', label: 'Hours by Division', icon: '🏢', defaultVisible: true, defaultType: 'donut' },
  { id: 'weeklyHeatmap', label: 'Weekly Heatmap', icon: '📅', defaultVisible: true, allowTypeChange: false },
  { id: 'topEmployees', label: 'Top Employees', icon: '👷', defaultVisible: true, defaultType: 'bar' },
  { id: 'hoursByOpCode', label: 'Hours by Op Code', icon: '📋', defaultVisible: true, defaultType: 'bar' },
  { id: 'hoursByShift', label: 'Hours by Shift', icon: '⏰', defaultVisible: true, defaultType: 'donut' },
  { id: 'weeklyHours', label: 'Weekly Hours', icon: '📅', defaultVisible: true, defaultType: 'bar' },
  { id: 'monthlyHours', label: 'Monthly Hours', icon: '📆', defaultVisible: true, defaultType: 'bar' },
];

/**
 * ActualSpentDashboard - Analytics Dashboard for Actual Spent Hours
 * Displays various charts, graphs, and metrics based on timesheet data
 */
const ActualSpentDashboard = ({ data, onClose }) => {
  const [selectedView, setSelectedView] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Real-time presence tracking (shows who's viewing this page)
  const { viewers, isConnected, connectionState } = usePresence('actualSpentDashboard', 'global', true);

  // Use the settings hook for personalization
  const {
    settings,
    updateSetting,
    toggleMetric,
    toggleChart,
    setChartType,
    resetToDefaults,
    isMetricVisible,
    isChartVisible,
    getChartType,
  } = useAnalyticsSettings('actual_spent_hours', AVAILABLE_METRICS, AVAILABLE_CHARTS);

  // Get date range from settings
  const dateRange = settings.dateRange || 'all';

  // Calculate analytics from data
  const analytics = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        totalRecords: 0,
        totalHours: 0,
        totalRegular: 0,
        totalOvertime: 0,
        totalDouble: 0,
        uniqueEmployees: 0,
        uniqueTrades: 0,
        uniqueDivisions: 0,
        dateRange: { start: null, end: null },
        hoursByDate: [],
        hoursByTrade: [],
        hoursByDivision: [],
        hoursByEmployee: [],
        hoursByOpCode: [],
        hoursByShift: [],
        cumulativeData: [],
        weeklyData: [],
        monthlyData: [],
      };
    }

    // Filter data by date range
    let filteredData = [...data];
    const now = new Date();
    if (dateRange === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filteredData = filteredData.filter(d => new Date(d.date) >= weekAgo);
    } else if (dateRange === 'month') {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      filteredData = filteredData.filter(d => new Date(d.date) >= monthAgo);
    } else if (dateRange === 'quarter') {
      const quarterAgo = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      filteredData = filteredData.filter(d => new Date(d.date) >= quarterAgo);
    } else if (dateRange === 'year') {
      const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
      filteredData = filteredData.filter(d => new Date(d.date) >= yearAgo);
    }

    // Basic metrics
    const totalRecords = filteredData.length;
    const totalHours = filteredData.reduce((sum, d) => sum + (parseFloat(d.total_hrs) || 0), 0);
    const totalRegular = filteredData.reduce((sum, d) => sum + (parseFloat(d.reg) || 0), 0);
    const totalOvertime = filteredData.reduce((sum, d) => sum + (parseFloat(d.t12) || 0), 0);
    const totalDouble = filteredData.reduce((sum, d) => sum + (parseFloat(d.dbl) || 0), 0);
    
    const uniqueEmployees = new Set(filteredData.map(d => d.empl_code).filter(Boolean)).size;
    const uniqueTrades = new Set(filteredData.map(d => d.trade).filter(Boolean)).size;
    const uniqueDivisions = new Set(filteredData.map(d => d.division).filter(Boolean)).size;

    // Date range
    const dates = filteredData.map(d => new Date(d.date)).filter(d => !isNaN(d.getTime()));
    const dateRangeInfo = {
      start: dates.length > 0 ? new Date(Math.min(...dates)) : null,
      end: dates.length > 0 ? new Date(Math.max(...dates)) : null,
    };

    // Hours by Date
    const hoursByDateMap = {};
    filteredData.forEach(d => {
      const date = d.date?.split('T')[0];
      if (date) {
        if (!hoursByDateMap[date]) {
          hoursByDateMap[date] = { date, regular: 0, overtime: 0, double: 0, total: 0 };
        }
        hoursByDateMap[date].regular += parseFloat(d.reg) || 0;
        hoursByDateMap[date].overtime += parseFloat(d.t12) || 0;
        hoursByDateMap[date].double += parseFloat(d.dbl) || 0;
        hoursByDateMap[date].total += parseFloat(d.total_hrs) || 0;
      }
    });
    const hoursByDate = Object.values(hoursByDateMap).sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));

    // Cumulative hours
    let cumulative = 0;
    const cumulativeData = hoursByDate.map(d => {
      cumulative += d.total;
      return { date: d.date, cumulative, daily: d.total };
    });

    // Hours by Trade
    const tradeMap = {};
    filteredData.forEach(d => {
      const trade = d.trade || 'Unassigned';
      if (!tradeMap[trade]) {
        tradeMap[trade] = { name: trade, total: 0, regular: 0, overtime: 0, double: 0, employees: new Set() };
      }
      tradeMap[trade].total += parseFloat(d.total_hrs) || 0;
      tradeMap[trade].regular += parseFloat(d.reg) || 0;
      tradeMap[trade].overtime += parseFloat(d.t12) || 0;
      tradeMap[trade].double += parseFloat(d.dbl) || 0;
      if (d.empl_code) tradeMap[trade].employees.add(d.empl_code);
    });
    const hoursByTrade = Object.values(tradeMap)
      .map(t => ({ ...t, employees: t.employees.size }))
      .sort((a, b) => b.total - a.total);

    // Hours by Division
    const divisionMap = {};
    filteredData.forEach(d => {
      const division = d.division || 'Unassigned';
      if (!divisionMap[division]) {
        divisionMap[division] = { name: division, total: 0, regular: 0, overtime: 0, double: 0 };
      }
      divisionMap[division].total += parseFloat(d.total_hrs) || 0;
      divisionMap[division].regular += parseFloat(d.reg) || 0;
      divisionMap[division].overtime += parseFloat(d.t12) || 0;
      divisionMap[division].double += parseFloat(d.dbl) || 0;
    });
    const hoursByDivision = Object.values(divisionMap).sort((a, b) => b.total - a.total);

    // Hours by Employee (Top 15)
    const employeeMap = {};
    filteredData.forEach(d => {
      const key = d.empl_code || 'Unknown';
      if (!employeeMap[key]) {
        employeeMap[key] = { code: key, name: d.empl_name || 'Unknown', total: 0, regular: 0, overtime: 0, double: 0, records: 0 };
      }
      employeeMap[key].total += parseFloat(d.total_hrs) || 0;
      employeeMap[key].regular += parseFloat(d.reg) || 0;
      employeeMap[key].overtime += parseFloat(d.t12) || 0;
      employeeMap[key].double += parseFloat(d.dbl) || 0;
      employeeMap[key].records += 1;
    });
    const hoursByEmployee = Object.values(employeeMap).sort((a, b) => b.total - a.total).slice(0, 15);

    // Hours by Op Code
    const opCodeMap = {};
    filteredData.forEach(d => {
      const opCode = d.op_code || 'Unassigned';
      if (!opCodeMap[opCode]) {
        opCodeMap[opCode] = { code: opCode, description: d.op_code_description || opCode, total: 0, regular: 0, overtime: 0, double: 0, records: 0 };
      }
      opCodeMap[opCode].total += parseFloat(d.total_hrs) || 0;
      opCodeMap[opCode].regular += parseFloat(d.reg) || 0;
      opCodeMap[opCode].overtime += parseFloat(d.t12) || 0;
      opCodeMap[opCode].double += parseFloat(d.dbl) || 0;
      opCodeMap[opCode].records += 1;
    });
    const hoursByOpCode = Object.values(opCodeMap).sort((a, b) => b.total - a.total).slice(0, 20);

    // Hours by Shift
    const shiftMap = {};
    filteredData.forEach(d => {
      const shift = d.shift || 'Unassigned';
      if (!shiftMap[shift]) {
        shiftMap[shift] = { shift, total: 0, regular: 0, overtime: 0, double: 0 };
      }
      shiftMap[shift].total += parseFloat(d.total_hrs) || 0;
      shiftMap[shift].regular += parseFloat(d.reg) || 0;
      shiftMap[shift].overtime += parseFloat(d.t12) || 0;
      shiftMap[shift].double += parseFloat(d.dbl) || 0;
    });
    const hoursByShift = Object.values(shiftMap).sort((a, b) => b.total - a.total);

    // Weekly aggregation
    const weeklyMap = {};
    filteredData.forEach(d => {
      const date = new Date(d.date);
      if (!isNaN(date.getTime())) {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        const weekKey = weekStart.toISOString().split('T')[0];
        if (!weeklyMap[weekKey]) {
          weeklyMap[weekKey] = { week: weekKey, total: 0, regular: 0, overtime: 0, double: 0, records: 0 };
        }
        weeklyMap[weekKey].total += parseFloat(d.total_hrs) || 0;
        weeklyMap[weekKey].regular += parseFloat(d.reg) || 0;
        weeklyMap[weekKey].overtime += parseFloat(d.t12) || 0;
        weeklyMap[weekKey].double += parseFloat(d.dbl) || 0;
        weeklyMap[weekKey].records += 1;
      }
    });
    const weeklyData = Object.values(weeklyMap).sort((a, b) => String(a.week || '').localeCompare(String(b.week || '')));

    // Monthly aggregation
    const monthlyMap = {};
    filteredData.forEach(d => {
      const date = new Date(d.date);
      if (!isNaN(date.getTime())) {
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyMap[monthKey]) {
          monthlyMap[monthKey] = { month: monthKey, total: 0, regular: 0, overtime: 0, double: 0, records: 0 };
        }
        monthlyMap[monthKey].total += parseFloat(d.total_hrs) || 0;
        monthlyMap[monthKey].regular += parseFloat(d.reg) || 0;
        monthlyMap[monthKey].overtime += parseFloat(d.t12) || 0;
        monthlyMap[monthKey].double += parseFloat(d.dbl) || 0;
        monthlyMap[monthKey].records += 1;
      }
    });
    const monthlyData = Object.values(monthlyMap).sort((a, b) => String(a.month || '').localeCompare(String(b.month || '')));

    return {
      totalRecords,
      totalHours,
      totalRegular,
      totalOvertime,
      totalDouble,
      uniqueEmployees,
      uniqueTrades,
      uniqueDivisions,
      dateRange: dateRangeInfo,
      hoursByDate,
      hoursByTrade,
      hoursByDivision,
      hoursByEmployee,
      hoursByOpCode,
      hoursByShift,
      cumulativeData,
      weeklyData,
      monthlyData,
    };
  }, [data, dateRange]);

  // Color palette
  const colors = {
    primary: '#6366f1',
    secondary: '#8b5cf6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    info: '#3b82f6',
    dark: '#1e293b',
    light: '#f1f5f9',
    chartColors: ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4'],
  };

  // Format number helper
  const formatNumber = (num, decimals = 1) => {
    if (num >= 1000000) return (num / 1000000).toFixed(decimals) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(decimals) + 'K';
    return num.toFixed(decimals);
  };

  // Bar Chart Component
  const BarChart = ({ data, valueKey, labelKey, title, maxBars = 10, stacked = false, showLegend = false }) => {
    const chartData = data.slice(0, maxBars);
    const maxValue = stacked 
      ? Math.max(...chartData.map(d => (d.regular || 0) + (d.overtime || 0) + (d.double || 0)), 1)
      : Math.max(...chartData.map(d => d[valueKey] || 0), 1);

    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {chartData.map((item, idx) => {
            if (stacked) {
              const regular = item.regular || 0;
              const overtime = item.overtime || 0;
              const double = item.double || 0;
              const total = regular + overtime + double;
              const regPct = maxValue > 0 ? (regular / maxValue) * 100 : 0;
              const otPct = maxValue > 0 ? (overtime / maxValue) * 100 : 0;
              const dblPct = maxValue > 0 ? (double / maxValue) * 100 : 0;

              return (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ width: '100px', fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item[labelKey]}>
                    {item[labelKey]}
                  </div>
                  <div style={{ flex: 1, display: 'flex', height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${regPct}%`, backgroundColor: colors.success, transition: 'width 0.5s ease' }} title={`Regular: ${regular.toFixed(1)}h`} />
                    <div style={{ width: `${otPct}%`, backgroundColor: colors.warning, transition: 'width 0.5s ease' }} title={`Overtime: ${overtime.toFixed(1)}h`} />
                    <div style={{ width: `${dblPct}%`, backgroundColor: colors.danger, transition: 'width 0.5s ease' }} title={`Double: ${double.toFixed(1)}h`} />
                  </div>
                  <div style={{ width: '60px', fontSize: '0.75rem', color: '#64748b', textAlign: 'right' }}>
                    {formatNumber(total)}h
                  </div>
                </div>
              );
            }

            const value = item[valueKey] || 0;
            const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
            return (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '100px', fontSize: '0.75rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item[labelKey]}>
                  {item[labelKey]}
                </div>
                <div style={{ flex: 1, height: '24px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${percentage}%`,
                    height: '100%',
                    backgroundColor: colors.chartColors[idx % colors.chartColors.length],
                    borderRadius: '4px',
                    transition: 'width 0.5s ease',
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: percentage > 20 ? '8px' : '0',
                  }}>
                    {percentage > 20 && <span style={{ color: 'white', fontSize: '0.7rem', fontWeight: '500' }}>{formatNumber(value)}h</span>}
                  </div>
                </div>
                <div style={{ width: '60px', fontSize: '0.75rem', color: '#64748b', textAlign: 'right' }}>
                  {percentage <= 20 && `${formatNumber(value)}h`}
                </div>
              </div>
            );
          })}
        </div>
        {showLegend && stacked && (
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', justifyContent: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: colors.success, borderRadius: '2px' }} />
              <span>Regular</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: colors.warning, borderRadius: '2px' }} />
              <span>Overtime</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: colors.danger, borderRadius: '2px' }} />
              <span>Double</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Donut Chart Component
  const DonutChart = ({ data, valueKey, labelKey, title }) => {
    const total = data.reduce((sum, d) => sum + (d[valueKey] || 0), 0);
    let currentAngle = 0;
    
    const segments = data.slice(0, 6).map((item, idx) => {
      const value = item[valueKey] || 0;
      const percentage = total > 0 ? (value / total) * 100 : 0;
      const angle = (percentage / 100) * 360;
      const startAngle = currentAngle;
      currentAngle += angle;
      return { ...item, percentage, angle, startAngle, color: colors.chartColors[idx % colors.chartColors.length] };
    });

    let gradient = 'conic-gradient(';
    segments.forEach((seg, idx) => {
      gradient += `${seg.color} ${seg.startAngle}deg ${seg.startAngle + seg.angle}deg`;
      if (idx < segments.length - 1) gradient += ', ';
    });
    gradient += ')';

    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{ position: 'relative', width: '120px', height: '120px' }}>
            <div style={{ width: '120px', height: '120px', borderRadius: '50%', background: total > 0 ? gradient : '#e2e8f0' }} />
            <div style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              width: '70px', height: '70px', borderRadius: '50%', backgroundColor: 'white',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ fontSize: '1rem', fontWeight: '700', color: colors.dark }}>{formatNumber(total)}</div>
              <div style={{ fontSize: '0.6rem', color: '#64748b' }}>hours</div>
            </div>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {segments.map((seg, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: seg.color }} />
                <span style={{ flex: 1, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{seg[labelKey]}</span>
                <span style={{ fontWeight: '500', color: colors.dark }}>{seg.percentage.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Pie Chart Component (similar to Donut but without center hole)
  const PieChart = ({ data, valueKey, labelKey, title }) => {
    const total = data.reduce((sum, d) => sum + (d[valueKey] || 0), 0);
    let currentAngle = 0;
    
    const segments = data.slice(0, 6).map((item, idx) => {
      const value = item[valueKey] || 0;
      const percentage = total > 0 ? (value / total) * 100 : 0;
      const angle = (percentage / 100) * 360;
      const startAngle = currentAngle;
      currentAngle += angle;
      return { ...item, percentage, angle, startAngle, color: colors.chartColors[idx % colors.chartColors.length] };
    });

    let gradient = 'conic-gradient(';
    segments.forEach((seg, idx) => {
      gradient += `${seg.color} ${seg.startAngle}deg ${seg.startAngle + seg.angle}deg`;
      if (idx < segments.length - 1) gradient += ', ';
    });
    gradient += ')';

    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{ position: 'relative', width: '120px', height: '120px' }}>
            <div style={{ width: '120px', height: '120px', borderRadius: '50%', background: total > 0 ? gradient : '#e2e8f0' }} />
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {segments.map((seg, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '2px', backgroundColor: seg.color }} />
                <span style={{ flex: 1, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{seg[labelKey]}</span>
                <span style={{ fontWeight: '500', color: colors.dark }}>{seg.percentage.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Line Chart Component
  const LineChart = ({ data, valueKey, labelKey, title, maxBars = 10 }) => {
    const chartData = data.slice(0, maxBars);
    const maxValue = Math.max(...chartData.map(d => d[valueKey] || 0), 1);

    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ position: 'relative', height: '200px', display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: '20px', width: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '0.65rem', color: '#94a3b8' }}>
            <span>{formatNumber(maxValue)}</span>
            <span>{formatNumber(maxValue * 0.5)}</span>
            <span>0</span>
          </div>
          <div style={{ marginLeft: '45px', flex: 1, height: '180px', position: 'relative', borderLeft: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
            {/* Line path */}
            <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflow: 'visible' }}>
              <polyline
                fill="none"
                stroke={colors.primary}
                strokeWidth="2"
                points={chartData.map((d, idx) => {
                  const x = (idx / (chartData.length - 1 || 1)) * 100;
                  const y = 100 - ((d[valueKey] || 0) / maxValue) * 100;
                  return `${x}%,${y}%`;
                }).join(' ')}
              />
              {chartData.map((d, idx) => {
                const x = (idx / (chartData.length - 1 || 1)) * 100;
                const y = 100 - ((d[valueKey] || 0) / maxValue) * 100;
                return (
                  <circle key={idx} cx={`${x}%`} cy={`${y}%`} r="4" fill={colors.primary} />
                );
              })}
            </svg>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', marginLeft: '45px', fontSize: '0.65rem', color: '#94a3b8' }}>
          {chartData.length > 0 && <span>{chartData[0][labelKey]}</span>}
          {chartData.length > 1 && <span>{chartData[chartData.length - 1][labelKey]}</span>}
        </div>
      </div>
    );
  };

  // Flexible Chart - renders based on chart type setting
  const FlexibleChart = ({ chartId, data, valueKey, labelKey, title, maxBars = 10, stacked = false, showLegend = false }) => {
    const chartType = getChartType(chartId, 'bar');
    
    switch (chartType) {
      case 'pie':
        return <PieChart data={data} valueKey={valueKey} labelKey={labelKey} title={title} />;
      case 'donut':
        return <DonutChart data={data} valueKey={valueKey} labelKey={labelKey} title={title} />;
      case 'line':
        return <LineChart data={data} valueKey={valueKey} labelKey={labelKey} title={title} maxBars={maxBars} />;
      case 'bar':
      default:
        return <BarChart data={data} valueKey={valueKey} labelKey={labelKey} title={title} maxBars={maxBars} stacked={stacked} showLegend={showLegend} />;
    }
  };

  // Area Chart Component
  const AreaChart = ({ data, title }) => {
    if (!data || data.length === 0) return null;
    const maxCumulative = Math.max(...data.map(d => d.cumulative || 0), 1);

    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>{title}</h4>
        <div style={{ position: 'relative', height: '200px', display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: '20px', width: '50px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', fontSize: '0.65rem', color: '#94a3b8' }}>
            <span>{formatNumber(maxCumulative)}</span>
            <span>{formatNumber(maxCumulative * 0.5)}</span>
            <span>0</span>
          </div>
          <div style={{ marginLeft: '55px', flex: 1, height: '180px', display: 'flex', alignItems: 'flex-end', gap: '1px', borderLeft: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0' }}>
            {data.slice(-60).map((d, idx) => {
              const height = maxCumulative > 0 ? (d.cumulative / maxCumulative) * 100 : 0;
              return (
                <div
                  key={idx}
                  style={{
                    flex: 1, height: `${height}%`,
                    background: `linear-gradient(180deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                    opacity: 0.8, transition: 'height 0.3s ease', borderRadius: '2px 2px 0 0',
                  }}
                  title={`${d.date}: ${formatNumber(d.cumulative)} total hrs`}
                />
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // Weekly Heatmap
  const WeeklyHeatmap = ({ data }) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayHours = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
    
    data.forEach(d => {
      const date = new Date(d.date);
      if (!isNaN(date.getTime())) {
        dayHours[date.getDay()] += parseFloat(d.total_hrs) || 0;
      }
    });

    const maxHours = Math.max(...Object.values(dayHours), 1);

    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>Hours by Day of Week</h4>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {days.map((day, idx) => {
            const hours = dayHours[idx];
            const intensity = maxHours > 0 ? hours / maxHours : 0;
            return (
              <div key={day} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  height: '80px', backgroundColor: `rgba(99, 102, 241, ${0.1 + intensity * 0.9})`,
                  borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '0.5rem',
                }}>
                  <span style={{ fontWeight: '600', fontSize: '0.9rem', color: intensity > 0.5 ? 'white' : colors.dark }}>
                    {formatNumber(hours)}
                  </span>
                </div>
                <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{day}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Progress Ring
  const ProgressRing = ({ value, max, label, color, size = 80 }) => {
    const percentage = max > 0 ? (value / max) * 100 : 0;
    const circumference = 2 * Math.PI * 35;
    const offset = circumference - (percentage / 100) * circumference;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
        <svg width={size} height={size} viewBox="0 0 80 80">
          <circle cx="40" cy="40" r="35" fill="none" stroke="#e2e8f0" strokeWidth="8" />
          <circle cx="40" cy="40" r="35" fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset} transform="rotate(-90 40 40)"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
          <text x="40" y="40" textAnchor="middle" dominantBaseline="middle" fill={colors.dark} fontSize="14" fontWeight="700">
            {percentage.toFixed(0)}%
          </text>
        </svg>
        <span style={{ fontSize: '0.75rem', color: '#64748b', textAlign: 'center' }}>{label}</span>
      </div>
    );
  };

  // Metric Card
  const MetricCard = ({ title, value, subtitle, icon, color, secondaryValue, secondaryLabel }) => (
    <div style={{
      backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', borderLeft: `4px solid ${color}`,
      display: 'flex', flexDirection: 'column', gap: '0.5rem',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '500' }}>{title}</span>
        <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
        <span style={{ fontSize: '1.75rem', fontWeight: '700', color: colors.dark }}>{value}</span>
      </div>
      {subtitle && <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{subtitle}</span>}
      {secondaryValue && (
        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
          <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{secondaryLabel}: </span>
          <span style={{ fontSize: '0.85rem', fontWeight: '600', color: color }}>{secondaryValue}</span>
        </div>
      )}
    </div>
  );

  // Hours Breakdown Grid
  const HourTypesGrid = () => {
    const totalAllHours = analytics.totalRegular + analytics.totalOvertime + analytics.totalDouble;
    
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <h4 style={{ margin: '0 0 1.25rem', color: colors.dark, fontSize: '1rem', fontWeight: '600' }}>Hours Breakdown</h4>
        <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
          <ProgressRing value={analytics.totalRegular} max={totalAllHours} label="Regular" color={colors.success} />
          <ProgressRing value={analytics.totalOvertime} max={totalAllHours} label="Overtime (1.5x)" color={colors.warning} />
          <ProgressRing value={analytics.totalDouble} max={totalAllHours} label="Double Time" color={colors.danger} />
        </div>
        <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
          <div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: colors.success }}>{formatNumber(analytics.totalRegular)}h</div>
            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Regular</div>
          </div>
          <div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: colors.warning }}>{formatNumber(analytics.totalOvertime)}h</div>
            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Overtime</div>
          </div>
          <div>
            <div style={{ fontSize: '1.25rem', fontWeight: '700', color: colors.danger }}>{formatNumber(analytics.totalDouble)}h</div>
            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>Double</div>
          </div>
        </div>
      </div>
    );
  };

  // Render tabs
  const renderOverview = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {isMetricVisible('totalHours') && (
          <MetricCard title="Total Hours" value={formatNumber(analytics.totalHours)} subtitle={`${analytics.totalRecords} records`} icon="⏱️" color={colors.primary}
            secondaryValue={`${formatNumber(analytics.totalHours / Math.max(analytics.uniqueEmployees, 1))}h avg`} secondaryLabel="Per Employee" />
        )}
        {isMetricVisible('teamSize') && (
          <MetricCard title="Team Size" value={analytics.uniqueEmployees} subtitle={`${analytics.uniqueTrades} trades`} icon="👥" color={colors.info}
            secondaryValue={analytics.uniqueDivisions} secondaryLabel="Divisions" />
        )}
        {isMetricVisible('regularHours') && (
          <MetricCard title="Regular Hours" value={formatNumber(analytics.totalRegular)} subtitle={`${((analytics.totalRegular / analytics.totalHours) * 100 || 0).toFixed(1)}% of total`} icon="✅" color={colors.success} />
        )}
        {isMetricVisible('overtimeHours') && (
          <MetricCard title="Overtime Hours" value={formatNumber(analytics.totalOvertime + analytics.totalDouble)} subtitle={`${((analytics.totalOvertime + analytics.totalDouble) / analytics.totalHours * 100 || 0).toFixed(1)}% of total`} icon="🔥" color={colors.warning} />
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {isChartVisible('cumulativeHours') && <AreaChart data={analytics.cumulativeData} title="📈 Cumulative Hours Over Time" />}
        {isMetricVisible('hoursBreakdown') && <HourTypesGrid />}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {isChartVisible('hoursByTrade') && <FlexibleChart chartId="hoursByTrade" data={analytics.hoursByTrade} valueKey="total" labelKey="name" title="🔧 Hours by Trade" stacked={true} showLegend={true} />}
        {isChartVisible('hoursByDivision') && <FlexibleChart chartId="hoursByDivision" data={analytics.hoursByDivision} valueKey="total" labelKey="name" title="🏢 Hours by Division" />}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '1rem' }}>
        {isChartVisible('weeklyHeatmap') && <WeeklyHeatmap data={data} />}
        {isChartVisible('topEmployees') && <FlexibleChart chartId="topEmployees" data={analytics.hoursByEmployee} valueKey="total" labelKey="name" title="👷 Top Employees" stacked={true} showLegend={true} maxBars={8} />}
      </div>
    </>
  );

  const renderEmployees = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard title="Active Employees" value={analytics.uniqueEmployees} icon="👤" color={colors.primary} />
        <MetricCard title="Avg Hours/Employee" value={formatNumber(analytics.totalHours / Math.max(analytics.uniqueEmployees, 1))} icon="📊" color={colors.info} />
        <MetricCard title="Top Performer" value={formatNumber(analytics.hoursByEmployee[0]?.total || 0)} subtitle={analytics.hoursByEmployee[0]?.name || 'N/A'} icon="🏆" color={colors.warning} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '1rem' }}>
        <BarChart data={analytics.hoursByEmployee} valueKey="total" labelKey="name" title="👷 Top 15 Employees by Hours" maxBars={15} stacked={true} showLegend={true} />
      </div>
    </>
  );

  const renderOperations = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard title="Unique Op Codes" value={analytics.hoursByOpCode.length} icon="🔢" color={colors.primary} />
        <MetricCard title="Most Used" value={analytics.hoursByOpCode[0]?.code || 'N/A'} subtitle={`${formatNumber(analytics.hoursByOpCode[0]?.total || 0)} hours`} icon="📌" color={colors.success} />
        <MetricCard title="Shifts Active" value={analytics.hoursByShift.length} icon="🔄" color={colors.info} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        {isChartVisible('hoursByOpCode') && <FlexibleChart chartId="hoursByOpCode" data={analytics.hoursByOpCode} valueKey="total" labelKey="code" title="📋 Hours by Operation Code" maxBars={15} />}
        {isChartVisible('hoursByShift') && <FlexibleChart chartId="hoursByShift" data={analytics.hoursByShift} valueKey="total" labelKey="shift" title="⏰ Hours by Shift" />}
      </div>
    </>
  );

  const renderTrends = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard title="Date Range" value={analytics.dateRange.start ? `${analytics.dateRange.start.toLocaleDateString()} - ${analytics.dateRange.end.toLocaleDateString()}` : 'N/A'} icon="📅" color={colors.primary} />
        <MetricCard title="Weekly Average" value={formatNumber(analytics.weeklyData.length > 0 ? analytics.weeklyData.reduce((a, b) => a + b.total, 0) / analytics.weeklyData.length : 0)} subtitle="hours per week" icon="📊" color={colors.info} />
        <MetricCard title="Peak Week" value={formatNumber(Math.max(...analytics.weeklyData.map(w => w.total), 0))} subtitle="highest weekly total" icon="📈" color={colors.success} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem', marginBottom: '1.5rem' }}>
        {isChartVisible('weeklyHours') && <FlexibleChart chartId="weeklyHours" data={analytics.weeklyData.slice(-12)} valueKey="total" labelKey="week" title="📅 Weekly Hours (Last 12 Weeks)" stacked={true} showLegend={true} />}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '1rem' }}>
        {isChartVisible('monthlyHours') && <FlexibleChart chartId="monthlyHours" data={analytics.monthlyData} valueKey="total" labelKey="month" title="📆 Monthly Hours Summary" stacked={true} showLegend={true} />}
      </div>
    </>
  );

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'employees', label: 'Employees', icon: '👥' },
    { id: 'operations', label: 'Operations', icon: '⚙️' },
    { id: 'trends', label: 'Trends', icon: '📈' },
  ];

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)',
      zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem',
    }}>
      <div style={{
        backgroundColor: '#f1f5f9', borderRadius: '16px', width: '100%', maxWidth: '1400px', maxHeight: '95vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      }}>
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem', background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700' }}>📊 Timesheet Analytics</h2>
            <p style={{ margin: '0.25rem 0 0', opacity: 0.9, fontSize: '0.85rem' }}>Comprehensive insights into actual spent hours</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <select value={dateRange} onChange={(e) => updateSetting('dateRange', e.target.value)}
              style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: 'none', backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white', cursor: 'pointer', fontSize: '0.85rem' }}>
              <option value="all" style={{ color: 'black' }}>All Time</option>
              <option value="week" style={{ color: 'black' }}>Last 7 Days</option>
              <option value="month" style={{ color: 'black' }}>Last 30 Days</option>
              <option value="quarter" style={{ color: 'black' }}>Last 90 Days</option>
              <option value="year" style={{ color: 'black' }}>Last Year</option>
            </select>
            <button
              type="button"
              onClick={() => setShowHelp(true)}
              style={{
                padding: '0.45rem 0.75rem', backgroundColor: 'rgba(255,255,255,0.22)', color: 'white',
                border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
              }}
            >
              ❓ Help
            </button>
            <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
            <SettingsButton onClick={() => setShowSettings(true)} />
            <button onClick={onClose} style={{
              padding: '0.5rem 1rem', backgroundColor: 'rgba(255, 255, 255, 0.2)', color: 'white',
              border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem',
            }}>✕ Close</button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', padding: '1rem 1.5rem 0', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0' }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setSelectedView(tab.id)} style={{
              padding: '0.75rem 1.25rem', backgroundColor: selectedView === tab.id ? colors.primary : 'transparent',
              color: selectedView === tab.id ? 'white' : '#64748b', border: 'none', borderRadius: '8px 8px 0 0',
              cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem',
            }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
          {analytics.totalRecords === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📭</div>
              <h3 style={{ margin: 0, color: colors.dark }}>No Data Available</h3>
              <p style={{ margin: '0.5rem 0 0' }}>Upload timesheet data to see analytics</p>
            </div>
          ) : (
            <>
              {selectedView === 'overview' && renderOverview()}
              {selectedView === 'employees' && renderEmployees()}
              {selectedView === 'operations' && renderOperations()}
              {selectedView === 'trends' && renderTrends()}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '0.75rem 1.5rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', color: '#94a3b8',
        }}>
          <span>Data range: {analytics.dateRange.start?.toLocaleDateString() || 'N/A'} - {analytics.dateRange.end?.toLocaleDateString() || 'N/A'}</span>
          <span>{analytics.totalRecords.toLocaleString()} records | {analytics.uniqueEmployees} employees | {formatNumber(analytics.totalHours)} total hours</span>
        </div>
      </div>
      
      {/* Settings Panel */}
      {showHelp && <TimeTrackerHelpModal onClose={() => setShowHelp(false)} />}

      <AnalyticsSettingsPanel
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        updateSetting={updateSetting}
        toggleMetric={toggleMetric}
        toggleChart={toggleChart}
        setChartType={setChartType}
        resetToDefaults={resetToDefaults}
        availableMetrics={AVAILABLE_METRICS}
        availableCharts={AVAILABLE_CHARTS}
      />
    </div>
  );
};

export default ActualSpentDashboard;

